// Method suitability assessment — PSD §6.5 (F6.5). Engine layer (framework-free).
/**
 * Check which valuation methods are suitable given the inputs.
 * Flags when lattice (path-dependent options) or Monte-Carlo (complex waterfalls) are needed.
 */
export function checkSuitability(inputs) {
    const flags = [];
    const cap = inputs.capTableSnapshot;
    const hasPreferred = cap.instruments.some((i) => i.kind === 'preferred');
    const hasParticipatingPref = cap.instruments.some((i) => i.kind === 'preferred' && i.participation !== null && i.participation !== 'nonParticipating');
    const hasConvertibles = cap.convertibles.length > 0;
    const hasComplexWaterfall = cap.instruments.filter((i) => i.kind === 'preferred').length > 2;
    const fdShares = cap.instruments.reduce((s, i) => s + (i.kind === 'pool' ? i.sharesAuthorized - i.sharesOutstanding : i.sharesOutstanding), 0);
    // OPM: always suitable for backsolve
    flags.push({
        method: 'opm',
        suitable: true,
        reason: 'OPM is the standard for backsolve from recent preferred transaction.',
    });
    // PWERM: suitable when multiple distinct outcomes (early-stage, binary prospects)
    flags.push({
        method: 'pwer',
        suitable: fdShares < 5_000_000 || hasConvertibles,
        reason: fdShares < 5_000_000
            ? 'Early-stage company — PWERM captures binary outcome scenarios.'
            : hasConvertibles
                ? 'Convertibles present — PWERM models conversion scenarios explicitly.'
                : 'Mature company — OPM preferred over PWERM.',
    });
    // Hybrid: suitable for growth-stage with both recent transaction and scenario uncertainty
    flags.push({
        method: 'hybrid',
        suitable: hasPreferred && hasParticipatingPref,
        reason: hasParticipatingPref
            ? 'Participating preferred creates non-linear payoffs — hybrid blends OPM precision with PWERM flexibility.'
            : 'Not needed — standard OPM suffices.',
    });
    // CVM: only suitable for very early-stage with no meaningful OPM inputs
    flags.push({
        method: 'cvm',
        suitable: fdShares < 1_000_000 && !hasPreferred,
        reason: fdShares < 1_000_000 && !hasPreferred
            ? 'Very early-stage, no preferred — CVM pro-rata is simplest defensible method.'
            : 'Too simplistic once preferred or meaningful share count exists.',
    });
    // Lattice needed for path-dependent features (vesting cliffs, anti-dilution)
    const needsLattice = cap.instruments.some((i) => (i.kind === 'option' || i.kind === 'warrant') && (i.strikePrice ?? 0) > 0 && i.vestingMonths !== null);
    // Monte-Carlo needed for complex waterfalls (multiple participating preferred tiers, convertibles)
    const needsMonteCarlo = hasComplexWaterfall && hasParticipatingPref && hasConvertibles;
    const recommended = needsMonteCarlo
        ? 'hybrid'
        : fdShares < 1_000_000 && !hasPreferred
            ? 'cvm'
            : fdShares < 5_000_000 || hasConvertibles
                ? 'pwer'
                : 'opm';
    return { recommended, flags, needsLattice, needsMonteCarlo };
}
//# sourceMappingURL=methodSuitability.js.map